import React, { useEffect, useState } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useAppTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";

const OPTIONS = [
  { value: "light", label: "Chiaro", hint: "L'aspetto attuale di Affidya.", Icon: Sun },
  { value: "dark", label: "Scuro", hint: "Più riposante con poca luce.", Icon: Moon },
  { value: "system", label: "Sistema", hint: "Segue le impostazioni del dispositivo.", Icon: Monitor },
];

/** true se nella pagina c'è già un altro dialog modale aperto (es. accettazione termini, 2FA): non lo copriamo. */
function otherModalOpen() {
  return Array.from(document.querySelectorAll('[role="dialog"], [role="alertdialog"]')).some(
    (el) => !el.hasAttribute("data-theme-choice") && el.getAttribute("data-state") !== "closed"
  );
}

/**
 * Dialog di primo accesso: scelta del tema (Chiaro / Scuro / Sistema).
 * Va montato SOLO nei layout delle aree autenticate che supportano il tema (mai su login o pagine pubbliche).
 *  - mostrato una sola volta per browser (flag `affidya-theme-chosen`, oppure `affidya-theme` già presente);
 *  - l'opzione che combacia con prefers-color-scheme è preselezionata (se non rilevabile: Chiaro);
 *  - ogni selezione si applica subito come anteprima; "Conferma" salva la scelta;
 *  - NON si chiude con Esc / click fuori / X: la scelta va confermata (comportamento volutamente semplice e robusto).
 */
export default function ThemeChoiceDialog() {
  const { needsThemeChoice, themable, preselectedTheme, previewTheme, confirmThemeChoice } = useAppTheme();
  const [ready, setReady] = useState(false);
  const [value, setValue] = useState(preselectedTheme);
  const wanted = needsThemeChoice && themable;

  // Aspetta che non ci siano altri dialog aperti (es. riaccettazione termini) prima di mostrarsi.
  useEffect(() => {
    if (!wanted) {
      setReady(false);
      return undefined;
    }
    if (!otherModalOpen()) {
      setReady(true);
      return undefined;
    }
    const id = window.setInterval(() => {
      if (!otherModalOpen()) {
        setReady(true);
        window.clearInterval(id);
      }
    }, 700);
    return () => window.clearInterval(id);
  }, [wanted]);

  const open = wanted && ready;

  // Anteprima immediata dell'opzione selezionata (compresa quella preselezionata all'apertura)
  useEffect(() => {
    if (!open) return undefined;
    previewTheme(value);
    return () => previewTheme(null);
  }, [open, value, previewTheme]);

  if (!open) return null;

  return (
    <Dialog open onOpenChange={() => { /* non si chiude senza conferma */ }}>
      <DialogContent
        data-theme-choice=""
        data-testid="theme-choice-dialog"
        hideCloseButton
        className="max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Scegli l'aspetto di Affidya</DialogTitle>
          <DialogDescription>
            Seleziona il tema che preferisci: l'anteprima si applica subito. Potrai cambiarlo in qualsiasi momento dal menu utente
            o con il pulsante sole/luna.
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={value}
          onValueChange={setValue}
          aria-label="Tema dell'interfaccia"
          className="grid gap-2"
          data-testid="theme-choice-group"
        >
          {OPTIONS.map(({ value: v, label, hint, Icon }) => (
            <label
              key={v}
              htmlFor={`theme-choice-${v}`}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors focus-within:ring-2 focus-within:ring-ring ${
                value === v ? "border-primary bg-primary/10" : "border-border hover:bg-accent"
              }`}
            >
              <RadioGroupItem id={`theme-choice-${v}`} value={v} data-testid={`theme-choice-${v}`} />
              <Icon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-foreground">{label}</span>
                <span className="text-xs text-muted-foreground">{hint}</span>
              </span>
            </label>
          ))}
        </RadioGroup>

        <DialogFooter>
          <Button type="button" data-testid="theme-choice-confirm" onClick={() => confirmThemeChoice(value)}>
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
